import type { PropsWithChildren, ReactNode } from 'react'
import { ScrollView, View } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { Icon } from '@/src/elements/media/Icon'
import { BarStepper } from '@/src/elements/stepper/bar'
import type { TStepperProps } from '@/src/elements/stepper/stepper.types'
import { Typography } from '@/src/elements/text/Typography'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON, type ICON as ICON_TYPE } from '@/src/general/enums/icons'

type TSubscribeJourneyLayoutProps = {
  testID?: string
  stepperOptions?: TStepperProps
  footer?: ReactNode
  icon?: ICON_TYPE
  title?: ReactNode
  subtitle?: ReactNode
  subtitleTestId?: string
  onBack?: () => void
  onClose?: () => void
}

export const SubscribeJourneyLayout = ({
  testID,
  stepperOptions,
  children,
  footer,
  icon,
  title,
  subtitle,
  subtitleTestId,
  onBack,
  onClose,
}: PropsWithChildren<TSubscribeJourneyLayoutProps>): ReactNode => {
  const showHeaderRow = !!onBack || !!onClose || !!stepperOptions

  return (
    <View testID={testID} className="max-w-screen flex-1 pt-safe pb-safe">
      <View className="max-w-screen w-full flex-1 flex-col gap-2 self-center">
        <ScrollView className="w-full grow px-2 py-4 xm:items-center" contentContainerClassName="grow">
          {showHeaderRow && (
            <View className="flex flex-row items-center justify-between gap-4">
              {onBack ? (
                <View testID={TEST_ID.COMMUNITY.JOIN.BACK_BUTTON}>
                  <Button
                    testID={TEST_ID.COMMUNITY.JOIN.BACK_BUTTON}
                    colors="primary"
                    size="md"
                    variant="faded"
                    isIconOnly
                    startIcon={ICON.ARROW_LEFT}
                    onPress={onBack}
                  />
                </View>
              ) : (
                <View className="h-12 w-12" />
              )}

              <View className="flex grow">{stepperOptions ? <BarStepper {...stepperOptions} /> : null}</View>

              {onClose ? (
                <Button
                  testID={TEST_ID.MODAL.CLOSE_BUTTON}
                  size="md"
                  colors="primary"
                  variant="faded"
                  isIconOnly
                  startIcon={ICON.CLOSE}
                  onPress={onClose}
                />
              ) : (
                <View className="h-12 w-12" />
              )}
            </View>
          )}
          <View className="flex h-full w-full flex-1 flex-col justify-between gap-6 sm:max-w-screen-md md:max-w-[500px]">
            {!!(icon || title || subtitle) && (
              <View className="mt-10 items-center gap-4 px-6">
                {!!icon && (
                  <Icon name={icon} size="xl" className="h-10 w-10 font-black text-primary-icon-contrast-lower" />
                )}
                {!!title && (
                  <Typography size="xl" center black>
                    {title}
                  </Typography>
                )}
                {!!subtitle && (
                  <Typography testID={subtitleTestId} center>
                    {subtitle}
                  </Typography>
                )}
              </View>
            )}
            {children}
            {footer}
          </View>
        </ScrollView>
      </View>
    </View>
  )
}
